import { nanoid } from 'nanoid'
import z from 'zod'

import { teamImageSchema, teamNameSchema } from '@/team'
import { emailSchema, entitySchema, unixTimestamp } from '@/utility'

export type TeamRef = {
  teamUid: string
  teamName: string
  teamImageUri?: string
}

export const teamRefSchema = z
  .object({
    teamUid: z.string(),
    teamName: teamNameSchema,
    teamImageUri: teamImageSchema,
  })
  .meta({ id: 'team-ref' }) satisfies z.ZodType<TeamRef>

/** User def for app usage */
export const userSchema = entitySchema
  .extend({
    theme: z.string().optional(),
    /** Email or other unique token identifier for performant auth lookup */
    userIndex: z.string().overwrite((s) => s.toLowerCase()),
    /** Federated auth provided must have a linked email */
    email: emailSchema,
    teams: teamRefSchema.array(),
    activeTeamId: z.string().nullable(),
    hasGithub: z.boolean().optional().default(false),
  })
  .meta({ id: 'user' })

export type User = z.infer<typeof userSchema>

export function defaultUser(
  initialState: Partial<User> & ({ email: string } | { userIndex: string }),
): User {
  const userIndex = initialState.userIndex ?? initialState.email ?? ''

  if (!userIndex) {
    throw new Error('Must provide either userIndex or email for a default user')
  }

  return {
    uid: nanoid(),
    userIndex,
    email: 'user@example.com',
    teams: [],
    hasGithub: false,
    activeTeamId: null,
    createdAt: unixTimestamp(new Date()),
    updatedAt: unixTimestamp(new Date()),
    ...initialState,
  }
}
